import { useState } from 'react'
import { ApiRequestError } from '../../../lib/api'
import type { ReviewQuestion } from '../../../stores/onboardingV2Store'

interface Props {
  question: ReviewQuestion
  onAnswer: (value: string) => Promise<void>
  onSkip: () => Promise<void>
}

// Type C (content contradiction): side-by-side comparison + radio choice +
// "Skip for now". Optional; max 1–2 per session (enforced server-side).
export default function ReviewContentQuestion({ question, onAnswer, onSkip }: Props) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const act = async (fn: () => Promise<void>) => {
    setBusy(true)
    setError(null)
    try {
      await fn()
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.error.message : 'Something went wrong — try again.')
    } finally {
      setBusy(false)
    }
  }

  const labelA = question.source_a?.label ?? 'Version A'
  const labelB = question.source_b?.label ?? 'Version B'

  return (
    <div className="border border-ob-accent/40 bg-ob-accent/5 p-4">
      <p className="mb-2 flex items-center gap-1.5 font-label text-[11px] uppercase tracking-wider text-ob-accent">
        <span className="material-symbols-outlined text-[16px]" aria-hidden="true">help</span>
        Quick question
      </p>
      <p className="mb-3 font-body text-sm text-ob-on-surface">
        {question.text || 'We found two versions of the same achievement.'}
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {[question.source_a, question.source_b].map((src, i) =>
          src ? (
            <div key={i} className="border border-ob-secondary/30 bg-ob-surface-card p-3">
              <p className="mb-1 font-label text-[10px] uppercase tracking-wider text-ob-secondary">
                {src.label}
              </p>
              <p className="font-body text-sm text-ob-on-surface">{src.text}</p>
            </div>
          ) : null,
        )}
      </div>
      <div className="mt-4 flex flex-col gap-2">
        <button type="button" disabled={busy} onClick={() => void act(() => onAnswer('use_a'))}
          className="min-h-[44px] border border-ob-ink px-4 py-2 text-start font-body text-sm text-ob-ink transition-colors hover:bg-ob-ink/5 disabled:opacity-50">
          Use {labelA}
        </button>
        <button type="button" disabled={busy} onClick={() => void act(() => onAnswer('use_b'))}
          className="min-h-[44px] border border-ob-ink px-4 py-2 text-start font-body text-sm text-ob-ink transition-colors hover:bg-ob-ink/5 disabled:opacity-50">
          Use {labelB}
        </button>
        <button type="button" disabled={busy} onClick={() => void act(() => onAnswer('keep_both'))}
          className="min-h-[44px] border border-ob-ink px-4 py-2 text-start font-body text-sm text-ob-ink transition-colors hover:bg-ob-ink/5 disabled:opacity-50">
          These are different — keep both
        </button>
        <button type="button" disabled={busy} onClick={() => void act(onSkip)}
          className="min-h-[44px] px-1 text-start font-body text-sm text-ob-secondary underline transition-colors hover:text-ob-ink disabled:opacity-50">
          Skip for now
        </button>
      </div>
      {error && (
        <p className="mt-3 font-body text-sm text-ob-error" role="alert">{error}</p>
      )}
    </div>
  )
}
